/**
 * KeycloakContext - Keycloak Authentication Provider
 * 
 * PURPOSE:
 * Manages Keycloak authentication state and provides authentication methods
 * to child components in the React component tree.
 * 
 * WHEN THIS IS USED:
 * This provider is ONLY mounted when authMode === 'keycloak' in AppRouter.
 * When authMode === 'okta', the Okta <Security> provider is used instead.
 * 
 * KEY FEATURES:
 * 1. Initializes Keycloak JS adapter
 * 2. Manages authentication state (authenticated, token, userInfo)
 * 3. Provides login/logout methods
 * 4. Supports email-first authentication with IdP hints
 * 5. Handles automatic token refresh
 * 6. Provides safe defaults when not initialized (dual-auth support)
 * 
 * DUAL AUTHENTICATION ARCHITECTURE:
 * Since both useOktaAuth() and useKeycloak() hooks are called in RouterWrapper
 * (React Hooks rules require unconditional calls), this hook must return
 * safe defaults when the KeycloakProvider is not mounted.
 * 
 * LOADING STATE MANAGEMENT:
 * The loading state prevents showing ReactHome while backend authentication 
 * is processing after the user returns from Keycloak/IdP authentication.
 * 
 * Flow:
 * 1. User returns from Keycloak with auth code
 * 2. Keycloak initializes → loading = true
 * 3. RouterWrapper detects authentication → calls /security/login
 * 4. Backend returns BUC token
 * 5. RouterWrapper calls completeBackendAuth() → loading = false
 * 6. User sees authenticated app (not ReactHome)
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import Keycloak from 'keycloak-js';
import { StateContext } from './StateContext';
import { 
    isTestEnvironment, 
    getRedirectUri, 
    isPublicRoute as checkIsPublicRoute,
    clearBrowserStorage 
} from './keycloak/helpers';
import { PUBLIC_ROUTES } from './keycloak/constants';
import { shouldActivateKeycloak } from '../utils/keycloakLocation';

// Constants
const KEYCLOAK_CONFIG = {
    url: 'https://smpservices-dev.eastus.cloudapp.azure.com',
    realm: 'buc-realm',
    clientId: 'buc-portal-client'
};

const INIT_OPTIONS = {
    checkLoginIframe: false,
    checkLoginIframeInterval: 0,
    enableLogging: true,
    pkceMethod: 'S256',
    flow: 'standard',
    responseMode: 'fragment',
    promiseType: 'native'
};

const TIMEOUTS = {
    FALLBACK: 10000, // 10 seconds
    TOKEN_REFRESH: 60000, // 1 minute
    TOKEN_REFRESH_THRESHOLD: 30 // seconds
};

const STORAGE_KEYS = {
    SHOW_EMAIL_DIALOG: 'showKeycloakEmailDialog',
    AUTH_MODE: 'authMode',
    I18N_LANGUAGE: 'i18nextLng'
};

// Create Keycloak context for provider/consumer pattern
const KeycloakContext = createContext();

/**
 * useKeycloak Hook
 * 
 * Custom hook to access Keycloak authentication context.
 * 
 * IMPORTANT: Returns safe defaults when provider is not mounted.
 * This is CRITICAL for dual authentication support because:
 * 
 * 1. RouterWrapper calls BOTH useOktaAuth() and useKeycloak() unconditionally
 *    (React Hooks rules: hooks must be called in the same order every render)
 * 
 * 2. When authMode === 'okta':
 *    - AppRouter mounts <Security> (Okta provider)
 *    - useOktaAuth() returns real Okta state
 *    - useKeycloak() is called but KeycloakProvider is NOT mounted
 *    - This hook returns safe defaults (authenticated: false, etc.)
 * 
 * 3. When authMode === 'keycloak':
 *    - AppRouter mounts <KeycloakProvider>
 *    - useKeycloak() returns real Keycloak state
 *    - useOktaAuth() returns defaults from Okta's provider (not mounted)
 * 
 * @returns {Object} Keycloak context with authentication state and methods
 */
export const useKeycloak = () => {
    const context = useContext(KeycloakContext);
    
    /**
     * Safe Defaults (Provider Not Mounted)
     * 
     * Return empty/false values when KeycloakProvider is not in component tree.
     * This allows RouterWrapper to safely call this hook even in Okta mode.
     */
    if (!context) {
        return {
            keycloak: null,
            authenticated: false,
            initialized: false,
            loading: false,
            token: null,
            userInfo: null,
            login: () => console.warn('Keycloak not initialized'),
            logout: () => console.warn('Keycloak not initialized'),
            loginWithEmail: () => console.warn('Keycloak not initialized'),
            completeBackendAuth: null
        };
    }
    
    return context;
};

/**
 * KeycloakProvider Component
 * 
 * React context provider that manages Keycloak authentication state.
 * 
 * LIFECYCLE:
 * 1. Component mounts → Initialize Keycloak JS adapter
 * 2. Keycloak.init() → Check for existing session (check-sso mode)
 * 3. If authenticated → Load user profile, set up token refresh
 * 4. If not authenticated → Set loading = false, show ReactHome
 * 5. User clicks login → Redirect to Keycloak
 * 6. User returns with auth code → Keycloak processes tokens
 * 7. RouterWrapper detects authentication → Calls backend
 * 8. Backend returns BUC token → completeBackendAuth() called
 * 9. Loading state cleared → User sees authenticated app
 * 
 * STATE MANAGEMENT:
 * - keycloak: The Keycloak JS adapter instance
 * - authenticated: Whether user has valid Keycloak session
 * - initialized: Whether Keycloak.init() has completed
 * - loading: Whether authentication is still processing
 * - processingBackendAuth: Whether backend auth call is in progress
 * 
 * LOADING STATE FLOW:
 * Initial: loading = true
 * ↓
 * Keycloak initialized (no session): loading = false → Show ReactHome
 * ↓
 * User logs in → Redirect to Keycloak
 * ↓
 * Return from Keycloak: authenticated = true, loading = true
 * ↓
 * RouterWrapper calls backend: processingBackendAuth = true
 * ↓
 * Backend responds: completeBackendAuth() → loading = false
 * ↓
 * Show authenticated app
 * 
 * PUBLIC ROUTES:
 * Some routes don't require authentication (e.g., /resetpassword, /eula.htm).
 * For these routes, we skip Keycloak initialization to avoid unnecessary
 * redirects and improve load time.
 * 
 * FALLBACK HANDLING:
 * Multiple fallback mechanisms ensure the app doesn't get stuck in loading:
 * 1. 10-second timeout: Create basic instance if init takes too long
 * 2. Network errors: Create basic instance, allow manual login attempt
 * 3. 404 errors: Try /auth suffix (older Keycloak versions)
 * 4. Login required: Normal state, create basic instance
 * 
 * TEST ENVIRONMENT:
 * When NODE_ENV === 'test' or REACT_APP_SKIP_KEYCLOAK_INIT === 'true':
 * - Skip Keycloak initialization
 * - Return mock values
 * - Allow unit tests to run without Keycloak server
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to wrap
 */
export const KeycloakProvider = ({ children }) => {
    /**
     * Authentication State
     * 
     * keycloak: Keycloak JS adapter instance (null until initialized)
     * authenticated: User has valid Keycloak session
     * initialized: Keycloak.init() has completed (success or failure)
     * loading: Still processing authentication (show spinner)
     * processingBackendAuth: Backend /security/login call in progress
     */
    const [keycloak, setKeycloak] = useState(null);
    const [authenticated, setAuthenticated] = useState(false);
    const [initialized, setInitialized] = useState(false);
    const [loading, setLoading] = useState(true);
    const [processingBackendAuth, setProcessingBackendAuth] = useState(false);

    /**
     * Global State Context Integration
     * 
     * Pull in optional state setters from StateContext (if available).
     * These are used to store user profile data after authentication.
     * 
     * Note: Using optional chaining because StateContext might not be
     * available in all parts of the component tree.
     */
    const stateCtx = useContext(StateContext) || {};
    const { setAvatarText, setName, setEmail } = stateCtx;

    /**
     * Test Environment Detection
     * 
     * Skip Keycloak initialization in test environments to allow unit tests
     * to run without requiring a Keycloak server.
     */
    const isTestEnv = isTestEnvironment();

    /**
     * Debug Authentication State Changes
     * 
     * Log state changes for debugging authentication flow.
     * Helps troubleshoot issues like:
     * - Stuck in loading state
     * - Authentication not detected
     * - Token issues
     */
    useEffect(() => {
        if (!isTestEnv) {
            console.log('🔐 KeycloakContext - Authentication state changed:', {
                authenticated,
                initialized,
                loading,
                hasKeycloak: !!keycloak
            });
        }
    }, [authenticated, initialized, loading, keycloak, isTestEnv]);

    /**
     * Helper: Analyze URL parameters for authentication flow
     */
    const analyzeUrlParameters = () => {
        const searchParams = new URLSearchParams(window.location.search);
        const urlParams = new URLSearchParams(window.location.hash.substring(1));
        
        return {
            shouldForceLogin: searchParams.get('prompt') === 'login',
            keycloakParam: searchParams.get('keycloak'),
            isKeycloakAllowed: shouldActivateKeycloak(searchParams),
            hasAuthCode: !!(urlParams.get('code') || urlParams.get('access_token')),
            authError: urlParams.get('error'),
            hasAuthError: (() => {
                const error = urlParams.get('error');
                return error && error !== 'login_required';
            })()
        };
    };

    /**
     * Helper: Create basic Keycloak instance without initialization
     */
    const createBasicKeycloakInstance = () => {
        return new Keycloak(KEYCLOAK_CONFIG);
    };

    /**
     * Helper: Set basic Keycloak state (unauthenticated)
     */
    const setBasicKeycloakState = (kcInstance) => {
        setKeycloak(kcInstance);
        setAuthenticated(false);
        setInitialized(true);
        setLoading(false);
    };

    /**
     * Helper: Clean authentication state after failure
     */
    const cleanAuthenticationState = (isCallbackFailure) => {
        console.log('🧹 Cleaning up authentication state due to initialization failure...');
        
        // 1. Clear all cookies (especially Keycloak session cookies)
        document.cookie.split(";").forEach((c) => {
            document.cookie = c
                .replace(/^ +/, "")
                .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
        });
        
        // 2. Clear localStorage (keep only essential non-auth items)
        const keysToKeep = isCallbackFailure 
            ? [STORAGE_KEYS.I18N_LANGUAGE] // Callback failure: only keep language
            : [STORAGE_KEYS.I18N_LANGUAGE, STORAGE_KEYS.AUTH_MODE]; // Initial failure: keep language + authMode
        
        const localStorageKeys = Object.keys(localStorage);
        localStorageKeys.forEach(key => {
            if (!keysToKeep.includes(key)) {
                localStorage.removeItem(key);
            }
        });
        
        // Ensure authMode is set to keycloak for initial failures
        if (!isCallbackFailure) {
            localStorage.setItem(STORAGE_KEYS.AUTH_MODE, 'keycloak');
        }
        
        // 3. Clear sessionStorage completely
        sessionStorage.clear();
        
        // 4. Only set email dialog flag for initial failures, NOT callback failures
        if (!isCallbackFailure) {
            window.localStorage.setItem(STORAGE_KEYS.SHOW_EMAIL_DIALOG, 'true');
            console.log('✅ Email dialog flag set for fresh login');
        } else {
            console.log('⚠️  Callback failed - will redirect to clean URL without setting email dialog flag');
        }
    };

    /**
     * Helper: Handle network/connection errors
     */
    const handleNetworkError = (isCallbackFailure, fallbackTimeout) => {
        console.warn('Keycloak server appears to be unreachable, creating basic instance for login');
        clearTimeout(fallbackTimeout);

        const kc = createBasicKeycloakInstance();
        setBasicKeycloakState(kc);
        
        if (!isCallbackFailure) {
            console.log('🔄 Ready for fresh login attempt - email dialog will be shown');
        } else {
            console.log('🔄 Redirecting to clean URL after callback failure...');
            window.location.href = window.location.pathname + '?keycloak=true';
        }
    };

    /**
     * Helper: Handle initial keycloak=true parameter cleanup
     * Clears storage on first load when keycloak parameter is detected
     * and the location allows Keycloak activation
     */
    const handleInitialKeycloakCleanup = (isKeycloakAllowed, hasAuthCode, hasAuthError) => {
        const showEmailDialogFlagExists = window.localStorage.getItem(STORAGE_KEYS.SHOW_EMAIL_DIALOG) === 'true';
        
        if (isKeycloakAllowed && !hasAuthCode && !hasAuthError && !showEmailDialogFlagExists) {
            console.log('🧹 Very first load with keycloak=true detected at allowed location - cleaning cookies and storage');
            
            // Clear browser storage, preserve authMode if it's keycloak
            const authMode = window.localStorage.getItem(STORAGE_KEYS.AUTH_MODE);
            const keysToKeep = authMode === 'keycloak' ? [STORAGE_KEYS.AUTH_MODE] : [];
            clearBrowserStorage(keysToKeep);
        }
    };

    /**
     * Helper: Check if email dialog should be shown instead of initializing
     * Returns true if we should skip initialization
     */
    const shouldShowEmailDialog = (hasAuthCode, hasAuthError, fallbackTimeout) => {
        const showEmailDialog = window.localStorage.getItem(STORAGE_KEYS.SHOW_EMAIL_DIALOG) === 'true';
        if (showEmailDialog && !hasAuthCode && !hasAuthError) {
            console.log('🔑 Email dialog flag detected - skipping initialization, will show email dialog');
            clearTimeout(fallbackTimeout);
            return true;
        }
        return false;
    };

    /**
     * Helper: Setup automatic token refresh
     * Refreshes token every minute, logs out if refresh fails
     */
    const setupTokenRefresh = (keycloakInstance) => {
        setInterval(() => {
            keycloakInstance.updateToken(TIMEOUTS.TOKEN_REFRESH_THRESHOLD).then((refreshed) => {
            }).catch(() => {
                console.error('Failed to refresh token - logging out');
                keycloakInstance.logout();
            });
        }, TIMEOUTS.TOKEN_REFRESH);
    };

    /**
     * Helper: Handle authenticated user post-initialization
     * Loads user profile and sets up token refresh
     */
    const handleAuthenticatedUser = async (keycloakInstance) => {
        try {
            await keycloakInstance.loadUserProfile();
            
            // Backend API call will be handled in RouterWrapper

            // Set up automatic token refresh - refresh when token expires in 30 seconds
            setupTokenRefresh(keycloakInstance);
        } catch (error) {
            console.warn('Failed to load user profile:', error);
        }
    };

    /**
     * Helper: Initialize Keycloak for email-first login
     * Returns true if initialization succeeds, false otherwise
     */
    const initializeKeycloakForEmailLogin = async (kcInstance) => {
        try {
            const isDevelopment = process.env.NODE_ENV === 'development';
            const redirectUri = getRedirectUri(isDevelopment, '?keycloak=true');
            
            // Initialize WITHOUT onLoad - this creates the adapter but doesn't redirect
            // We'll manually call login() right after
            await kcInstance.init({
                // NO onLoad parameter - prevents automatic redirect
                redirectUri: redirectUri,
                ...INIT_OPTIONS
            });
            
            setInitialized(true);
            return true;
        } catch (error) {
            console.error('Failed to initialize Keycloak:', error);
            alert('Failed to initialize authentication. Please refresh the page and try again.');
            return false;
        }
    };

    useEffect(() => {
        // Don't initialize Keycloak in test environments
        if (process.env.NODE_ENV === 'test' || process.env.REACT_APP_SKIP_KEYCLOAK_INIT === 'true') {
            return;
        }

        // Skip Keycloak initialization on public routes that don't require authentication
        const currentPath = window.location.pathname;
        const isPublicRouteCheck = checkIsPublicRoute(currentPath, PUBLIC_ROUTES);
        
        if (isPublicRouteCheck) {
            const kc = createBasicKeycloakInstance();
            setBasicKeycloakState(kc);
            return;
        }

        // Set a timeout to ensure we don't stay in loading state forever
        const fallbackTimeout = setTimeout(() => {
            console.warn('Keycloak initialization taking too long, creating basic instance');
            const kcTimeout = createBasicKeycloakInstance();
            setBasicKeycloakState(kcTimeout);
        }, TIMEOUTS.FALLBACK);

        const initKeycloak = async () => {
            console.log('Starting Keycloak initialization...');
            try {
                // Analyze URL parameters for authentication flow control
                const urlAnalysis = analyzeUrlParameters();
                const { shouldForceLogin, isKeycloakAllowed, hasAuthCode, authError, hasAuthError } = urlAnalysis;

                // CLEAN STATE ON FIRST KEYCLOAK LOAD (before ReactHome processes it)
                handleInitialKeycloakCleanup(isKeycloakAllowed, hasAuthCode, hasAuthError);

                // Configure Keycloak with settings for self-signed certificates and local development
                const kc = createBasicKeycloakInstance();

                // CHECK FOR EMAIL DIALOG FLAG - Skip initialization if email dialog should be shown
                if (shouldShowEmailDialog(hasAuthCode, hasAuthError, fallbackTimeout)) {
                    setKeycloak(kc);
                    setAuthenticated(false);
                    setInitialized(false);  // CRITICAL: Leave as false so loginWithEmail knows to call init()
                    setLoading(false);
                    // Don't clear the flag here - let ReactHome handle it
                    return;
                }

                // If prompt=login, clear existing session to force fresh login
                if (shouldForceLogin) {
                    console.log('🔐 prompt=login detected - will force fresh login after initialization');
                }

                // Initialize Keycloak - use check-sso to detect existing sessions
                // In development, React dev server runs on port 3000 but we need to redirect to proxy port 8415
                const isDevelopment = process.env.NODE_ENV === 'development';
                const redirectUri = getRedirectUri(isDevelopment);
                
                await kc.init({
                    onLoad: 'check-sso',  // Always check for existing sessions (auto-login on refresh)
                    redirectUri: redirectUri,
                    ...INIT_OPTIONS
                });

                clearTimeout(fallbackTimeout);
                setKeycloak(kc);
                // Check for actual authentication status after initialization
                const isAuthenticated = kc.authenticated || false;
                
                // If prompt=login is set, clear the session and force fresh login
                if (shouldForceLogin && isAuthenticated) {
                    console.log('🔐 Clearing existing session due to prompt=login parameter');
                    // Clear the session
                    kc.clearToken();
                    // Remove prompt parameter from URL to prevent infinite loop
                    const searchParams = new URLSearchParams(window.location.search);
                    searchParams.delete('prompt');
                    const newUrl = window.location.pathname + (searchParams.toString() ? '?' + searchParams.toString() : '');
                    window.history.replaceState({}, document.title, newUrl);
                    // Set flag to show email dialog after clearing session
                    window.localStorage.setItem(STORAGE_KEYS.SHOW_EMAIL_DIALOG, 'true');
                    // Reload to reinitialize without existing session
                    console.log('🔄 Reloading to start fresh login flow...');
                    window.location.reload();
                    return;
                }
                
                setAuthenticated(isAuthenticated);
                setInitialized(true);
                
                // If authenticated and returning from auth flow, keep loading state
                // to prevent showing ReactHome while backend processes authentication
                if (isAuthenticated && (hasAuthCode || hasAuthError)) {
                    setProcessingBackendAuth(true);
                } else {
                    setLoading(false);
                }

                // Clean up URL if we successfully authenticated
                // CRITICAL: Preserve keycloak=true parameter so app stays in Keycloak mode
                if (isAuthenticated && (hasAuthCode || hasAuthError)) {
                    const newUrl = window.location.pathname + '?keycloak=true';
                    window.history.replaceState({}, document.title, newUrl);
                }

                // Load user profile if authenticated (but don't call backend here)
                if (isAuthenticated) {
                    await handleAuthenticatedUser(kc);
                }

                // Handle unauthenticated state
                if (!isAuthenticated) {
                    // Clean up login_required error from URL (it's not a real error)
                    if (authError === 'login_required') {
                        window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
                    }
                    
                }
            } catch (error) {
                console.error('Keycloak initialization failed:', error);

                // Check if this is a callback failure (auth code present in URL)
                const urlParams = new URLSearchParams(window.location.hash.substring(1));
                const hasAuthCode = urlParams.get('code') || urlParams.get('access_token');
                const isCallbackFailure = !!hasAuthCode;

                if (isCallbackFailure) {
                    console.log('🔴 Auth callback failed (400 error during token exchange) - cleaning up and redirecting to fresh login');
                } else {
                    console.log('🔴 Initial Keycloak load failed - cleaning up for fresh start');
                }

                // Clear all authentication state to allow fresh retry
                cleanAuthenticationState(isCallbackFailure);

                // Handle network/connection errors (server unreachable)
                if (error.message && (error.message.includes('NetworkError') || error.message.includes('Failed to fetch') || error.message.includes('ECONNREFUSED'))) {
                    handleNetworkError(isCallbackFailure, fallbackTimeout);
                    return;
                }

                // Since we're not using 'check-sso', login_required errors shouldn't occur
                // but we'll keep this for backward compatibility
                if (error.error === 'login_required' || (error.message && error.message.includes('login_required'))) {
                    clearTimeout(fallbackTimeout);
                    const kc = createBasicKeycloakInstance();
                    setBasicKeycloakState(kc);
                    return;
                }

                // Check if it's a URL/404 error and suggest alternatives
                if (error.message && (error.message.includes('404') || error.message.includes('Not Found'))) {
                    console.warn('Keycloak URL might be incorrect. Trying fallback configurations...');

                    // Try with /auth suffix (for older Keycloak versions)
                    try {
                        const kcFallback = new Keycloak({
                            url: 'https://smpservices-dev.eastus.cloudapp.azure.com/auth',
                            realm: 'buc-realm',
                            clientId: 'buc-portal-client'
                        });

                        const isDev = process.env.NODE_ENV === 'development';
                        await kcFallback.init({
                            onLoad: 'check-sso',
                            redirectUri: getRedirectUri(isDev),
                            checkLoginIframe: false,
                            enableLogging: true
                        });

                        clearTimeout(fallbackTimeout);
                        setKeycloak(kcFallback);
                        const isFallbackAuthenticated = kcFallback.authenticated || false;
                        setAuthenticated(isFallbackAuthenticated);
                        setInitialized(true);
                        setLoading(false);
                        return;
                    } catch (fallbackError) {
                        console.error('Fallback URL also failed:', fallbackError);
                    }
                }

                // Set a fallback state to allow the app to continue
                console.warn('Setting fallback state due to Keycloak initialization failure');
                clearTimeout(fallbackTimeout);
                const kcBasic = createBasicKeycloakInstance();
                setBasicKeycloakState(kcBasic);
            }
        };

        initKeycloak();

        // Cleanup function to clear timeout if component unmounts
        return () => {
            clearTimeout(fallbackTimeout);
        };
    }, [setAvatarText, setName, setEmail]);

    const login = () => {
        console.log('Login button clicked - Keycloak state:', {
            keycloak: !!keycloak,
            initialized,
            authenticated,
            loading
        });

        if (keycloak && initialized) {
            const isDevelopment = process.env.NODE_ENV === 'development';
            const redirectUri = getRedirectUri(isDevelopment);
            keycloak.login({
                redirectUri: redirectUri
            });
        } else {
            console.warn('Keycloak not initialized, cannot perform login', {
                hasKeycloak: !!keycloak,
                initialized,
                loading
            });
        }
    };

    // Email-first login with IdP hint for domain-based routing
    const loginWithEmail = async (email, idpHint) => {
        console.log('Email-first login initiated:', {
            email,
            idpHint,
            keycloak: !!keycloak,
            initialized,
            authenticated,
            loading,
            hasLoginMethod: keycloak && typeof keycloak.login === 'function'
        });

        // Store reference to current keycloak instance for this function
        let kcInstance = keycloak;

        if (!kcInstance) {
            console.error('Keycloak instance is null or undefined');
            alert('Authentication system not ready. Please refresh the page and try again.');
            return;
        }

        // Email-first flow: Check if Keycloak has been initialized
        // The login() method exists from the constructor, but won't work until init() is called
        if (!initialized) {
            console.log('Email-first login: Keycloak not initialized yet - initializing now...');
            const success = await initializeKeycloakForEmailLogin(kcInstance);
            if (!success) return;
        }

        console.log('Launching Keycloak login with IdP hint:', idpHint);
        const isDevelopment = process.env.NODE_ENV === 'development';
        // CRITICAL: Include ?keycloak=true in redirectUri so app knows to use Keycloak mode after auth
        const redirectUri = getRedirectUri(isDevelopment, '?keycloak=true');
        
        // Double-check keycloak instance is valid before calling login
        if (typeof kcInstance.login !== 'function') {
            console.error('❌ Keycloak instance is missing login method');
            console.error('Current keycloak state:', {
                typeofKcInstance: typeof kcInstance,
                hasLogin: typeof kcInstance.login
            });
            alert('Authentication system is not ready. Please refresh the page and try again.');
            return;
        }
        
        try {
            const loginOptions = {
                redirectUri: redirectUri,
                loginHint: email,      // Pre-fills email at IdP if supported
                idpHint: idpHint       // Routes to specific IdP (test-keycloak or okta)
            };
            
            console.log('🚀 Calling keycloak.login() with options:', loginOptions);
            
            kcInstance.login(loginOptions);
        } catch (error) {
            console.error('Error calling keycloak.login:', error);
            console.error('Error details:', {
                message: error.message,
                stack: error.stack,
                name: error.name
            });
            alert('Failed to start authentication. Please refresh the page and try again.');
        }
    };

    const logout = () => {
        if (keycloak && initialized) {
            const isDevelopment = process.env.NODE_ENV === 'development';
            const redirectUri = getRedirectUri(isDevelopment);
            keycloak.logout({
                redirectUri: redirectUri
            });
        } else {
            console.warn('Keycloak not initialized, cannot perform logout');
        }
    };

    // Helper to complete backend authentication and clear loading state
    const completeBackendAuth = useCallback(() => {
        setProcessingBackendAuth(false);
        setLoading(false);
    }, []);

    // Provide different values based on environment
    const value = isTestEnv ? {
        keycloak: null,
        authenticated: false,
        initialized: true,
        loading: false,
        login: () => { },
        loginWithEmail: () => { },
        logout: () => { },
        completeBackendAuth: () => { },
        isReady: true,
        canLogin: true,
        token: null,
        userInfo: null,
        refreshToken: null,
        getProfile: async () => null,
    } : {
        keycloak,
        authenticated,
        initialized,
        loading: loading || processingBackendAuth,
        login,
        loginWithEmail,
        logout,
        completeBackendAuth,
        // Additional helper values
        isReady: initialized && !loading && !processingBackendAuth,
        canLogin: keycloak && initialized && !loading && !processingBackendAuth,
        // Token and user info from Keycloak instance
        token: keycloak?.token || null,
        userInfo: keycloak?.profile || null,
        refreshToken: keycloak?.refreshToken || null,
        // Helper function to get user profile
        getProfile: async () => {
            if (keycloak && authenticated) {
                try {
                    return await keycloak.loadUserProfile();
                } catch (error) {
                    console.error('Failed to load user profile:', error);
                    return null;
                }
            }
            return null;
        }
    };

    return (
        <KeycloakContext.Provider value={value}>
            {children}
        </KeycloakContext.Provider>
    );
};
